import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { ROLE_ROUTES } from "@utils/constants";
import ValidatorSidebar from "@components/shared/ValidatorSidebar";

const ValidatorLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { role, user } = useSelector((store) => store.auth);
  const destinationRoute = ROLE_ROUTES[role] || ROLE_ROUTES.DEFAULT;

  // Detect route type
  const isProjectPage = location.pathname === destinationRoute || location.pathname === `${destinationRoute}/`;
  const isFolderPage = location.pathname === `${destinationRoute}/folders`;
  const isFilePage = location.pathname === `${destinationRoute}/folders/files`;
  const isSentencePage = location.pathname === `${destinationRoute}/folders/files/sentences`;

  // Sidebar state
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  // Header title + subtitle
  const headerTitle = isProjectPage
    ? <span><span className="text-[#ec7026] shadow-sm">Validator</span> Dashboard Projects</span>
    : isFolderPage
      ? <span><span className="text-[#ec7026] shadow-sm">Validator</span> Dashboard Folders</span>
      : isFilePage
        ? <span><span className="text-[#ec7026] shadow-sm">Validator</span> Dashboard Files</span>
        : <span><span className="text-[#ec7026] shadow-sm">Validator</span> Dashboard Sentences</span>;

  const headerSubtitle = isProjectPage
    ? `Manage your Validator projects and settings`
    : isFolderPage
      ? `Manage your Validator folders and settings`
      : isFilePage
        ? `Manage your Validator files and settings`
        : `Manage your Validator sentences and settings`;

  // Navigation handlers
  const handleClickBack = () => {
    if (location.pathname.includes("/sentences")) {
      navigate(`${destinationRoute}/folders/files`);
    } else if (location.pathname.includes("/files")) {
      navigate(`${destinationRoute}/folders`);
    } else if (location.pathname.includes("/folders")) {
      navigate(destinationRoute);
    } else {
      navigate(-1);
    }
  };

  const handleClickHome = () => navigate(destinationRoute);

  return (
    <div className="flex min-h-screen bg-gray-50/50">
      {/* ✅ Sidebar - Collapsible */}
      <div
        className={`transition-all duration-300 bg-white border-r border-gray-200 z-20 ${isSidebarOpen ? "w-1/6" : "w-0"
          } overflow-hidden`}
      >
        <ValidatorSidebar />
      </div>

      {/* ✅ Main Content */}
      <div className="flex-1 flex flex-col transition-all duration-300">
        {/* ✅ Header */}
        <div
          className={`fixed top-16 bg-white border-b border-gray-200 shadow-sm z-30 transition-all duration-300 ${isSidebarOpen ? "left-[16.666%] w-[83.334%]" : "left-0 w-full"
            }`}
        >
          <div className="w-full px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-col md:flex-row gap-4 justify-between items-center">

              {/* ✅ Left Section: Toggle + Title */}
              <div className="flex flex-row items-start">
                {/* Sidebar Toggle Button */}
                <button
                  onClick={toggleSidebar}
                  className="p-3 focus:outline-none text-gray-700 hover:text-[#ec7026] transition-colors duration-200"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>

                <div className="flex flex-col">
                  <h1 className="text-2xl font-['Semantic/Strong'] font-bold text-gray-900">{headerTitle}</h1>
                  <p className="text-sm text-gray-500 font-['Semantic/Strong']">{headerSubtitle}</p>
                </div>
              </div>

              {/* ✅ Right Section: Buttons */}
              <div className="flex flex-wrap sm:flex-nowrap gap-2 items-center">
                <button
                  onClick={handleClickHome}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
                >
                  <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                    />
                  </svg>
                  <span>Home</span>
                </button>

                <button
                  onClick={handleClickBack}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
                >
                  <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  <span>Back</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ✅ Main Content Area */}
        <div
          className={`flex-1 w-full overflow-y-auto p-4 sm:p-6 lg:p-8 pt-[144px] mt-28 transition-all duration-300 `} >
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default ValidatorLayout;
